import * as React from "react";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import { Employee, Product, ProductCategory } from "../../types";
import styled from "styled-components";
import { useGetProductCategories } from "../../hooks/productCategoryHooks";
import { useGetProductMainCategories } from "../../hooks/productMainCategoryHooks";
import {
    Card,
    CardActions,
    FormControl,
    MenuItem,
    Select,
    SelectChangeEvent,
    Typography,
} from "@mui/material";
import { Shapes } from "lucide-react";
import { Translatable } from "../NewProductPage/ProductCategoryForm";

type Props = {
    changedProduct: Product;
    setChangedProduct: (product: Product) => void;
    employee: Employee | undefined;
};

export const EditProductCategory = (props: Props) => {
    const { data: productCategories } = useGetProductCategories();
    const { data: mainCategories } = useGetProductMainCategories();
    const [selectedMainCategory, setSelectedMainCategory] = React.useState<string | null>(null);

    const handleProductCategoryChange = (data: ProductCategory) => {
        // Remove if already in list
        if (props.changedProduct.categories?.includes(data.id)) {
            props.setChangedProduct({
                ...props.changedProduct,
                categories: props.changedProduct.categories.filter(
                    category => category !== data.id,
                ),
            });
            // Add if not in list
        } else {
            props.setChangedProduct({
                ...props.changedProduct,
                categories: [...(props.changedProduct.categories ?? []), data.id],
            });
        }
    };

    return (
        <Container>
            <Card
                variant="outlined"
                sx={{
                    borderRadius: "30px",
                    width: "90%",
                    overflowY: "auto",
                    maxHeight: "80vh",
                    padding: "5px",
                    marginBottom: "30px",
                }}
            >
                <StyledCardActions>
                    <IconActionsWrapper>
                        <Shapes />{" "}
                        <Typography
                            fontFamily={"'Poppins', sans-serif"}
                            fontSize={18}
                            color="#8c8c8c"
                            fontWeight={600}
                        >
                            Product Type
                        </Typography>
                    </IconActionsWrapper>
                </StyledCardActions>
                <ChipsContainer>
                    {mainCategories?.map(mainCategory => (
                        <Chip
                            key={mainCategory.id}
                            clickable
                            label={(() => {
                                switch (props.employee?.language) {
                                    case "en":
                                        return mainCategory.nameEN;
                                    case "no":
                                        return mainCategory.nameNO;
                                    case "dk":
                                        return mainCategory.nameDK;
                                    case "fi":
                                        return mainCategory.nameFI;
                                    case "sv":
                                        return mainCategory.name;
                                    default:
                                        return mainCategory.nameEN;
                                }
                            })()}
                            onClick={() => {
                                if (selectedMainCategory === mainCategory.id) {
                                    setSelectedMainCategory(null); // Stäng om man klickar på samma
                                } else {
                                    setSelectedMainCategory(mainCategory.id); // Öppna ny
                                }
                            }}
                            color={selectedMainCategory === mainCategory.id ? "primary" : "default"}
                            sx={{
                                fontFamily: '"Yllw Sans a1 241219", sans-serif',

                                backgroundColor:
                                    selectedMainCategory === mainCategory.id ? "black" : "white",

                                border: "1px solid black",
                            }}
                        />
                    ))}
                </ChipsContainer>
            </Card>
            {selectedMainCategory && (
                <Card
                    variant="outlined"
                    sx={{
                        borderRadius: "30px",
                        width: "90%",
                        overflowY: "auto",
                        maxHeight: "80vh",
                        padding: "5px",
                        marginBottom: "30px",
                    }}
                >
                    <StyledCardActions>
                        <IconActionsWrapper>
                            <Shapes />{" "}
                            <Typography
                                fontFamily={"'Poppins', sans-serif"}
                                fontSize={18}
                                color="#8c8c8c"
                                fontWeight={600}
                            >
                                Subcategory
                            </Typography>
                        </IconActionsWrapper>
                    </StyledCardActions>
                    <ChipsContainer>
                        {productCategories
                            ?.filter(data => data.category === selectedMainCategory)
                            .map(data => (
                                <Chip
                                    key={data.id}
                                    clickable
                                    onClick={() => {
                                        handleProductCategoryChange(data);
                                    }}
                                    color={
                                        props.changedProduct.categories?.includes(data.id)
                                            ? "primary"
                                            : "default"
                                    }
                                    label={(() => {
                                        switch (props.employee?.language) {
                                            case "en":
                                                return data.nameEN;
                                            case "no":
                                                return data.nameNO;
                                            case "dk":
                                                return data.nameDK;
                                            case "fi":
                                                return data.nameFI;
                                            case "sv":
                                                return data.name;
                                            default:
                                                return data.nameEN;
                                        }
                                    })()}
                                    sx={{
                                        fontFamily: '"Yllw Sans a1 241219", sans-serif',
                                        backgroundColor: props.changedProduct.categories?.includes(
                                            data.id,
                                        )
                                            ? "black"
                                            : "white",
                                        color: props.changedProduct.categories?.includes(data.id)
                                            ? "white"
                                            : "black",
                                        border: props.changedProduct.categories?.includes(data.id)
                                            ? "none"
                                            : "1px solid black",
                                    }}
                                />
                            ))}
                    </ChipsContainer>
                </Card>
            )}
        </Container>
    );
};

const ListItem = styled.li`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: space-between;
    margin: 0.5rem;
`;
const IconActionsWrapper = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: center;
    gap: 5px;
`;
const StyledCardActions = styled(CardActions)`
    display: flex;
    width: 100%;
    font-size: 3rem;
    border-bottom: 1px solid #ebebeb;
    //background-color: #ebebeb;
    align-items: center;
`;
const Container = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100vh;
`;
const ChipsContainer = styled.div`
    margin-top: 15px;
    margin-bottom: 30px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 5px;
`;
